import { getSpellResourceUiModel, SPELL_RESOURCE_SURFACE_ANY } from './spellResourceCompatibility';
import { getScalarSpellProgressionMaxCastLevel, syncSpellSettings } from './spellSystem';
import { getMaxSpellLevel, getSpellCastInfo } from './sheetCasting';
import {
  applyBoundResourceSpend,
  recoverDirectSpellResources,
  SPELL_BINDING_SURFACE_ANY,
  SPELL_BINDING_SURFACE_SHEET,
} from '../systems/resourceEngine';

export function resolveSpellAtLevel(spell, castLevel, characterLevel) {
  if (!spell) return spell;

  const baseLevel = Number.isFinite(spell?.level) ? spell.level : 0;
  const resolvedLevel = Number.isFinite(castLevel) ? castLevel : baseLevel;
  const levelsAbove = Math.max(0, resolvedLevel - baseLevel);

  let resolvedSpell = { ...spell };

  if (levelsAbove > 0 && spell.upcastPattern && spell.upcastPattern !== 'none' && spell.upcastPattern !== 'cantrip') {
    if (spell.upcastPattern === 'targets_per_level' && spell.upcastTargets) {
      resolvedSpell.maxTargets = (spell.maxTargets || 1) + (spell.upcastTargets * levelsAbove);
    }
    if (spell.upcastPattern === 'flat_per_level' && spell.upcastFlat) {
      resolvedSpell._upcastFlatBonus = spell.upcastFlat * levelsAbove;
    }
  }

  if (spell.upcastPattern === 'cantrip' && characterLevel && spell.levelData) {
    const thresholds = Object.keys(spell.levelData).map(Number).sort((left, right) => left - right);
    let bestThreshold = null;
    thresholds.forEach((threshold) => {
      if (characterLevel >= threshold) bestThreshold = threshold;
    });
    if (bestThreshold !== null) {
      resolvedSpell = { ...resolvedSpell, ...(spell.levelData?.[bestThreshold] || {}) };
    }
  }

  if (spell.upcastPattern !== 'cantrip' && spell.levelData?.[resolvedLevel]) {
    resolvedSpell = { ...resolvedSpell, ...(spell.levelData[resolvedLevel] || {}) };
  }

  const resolvedDice = (() => {
    if (spell.upcastPattern === 'cantrip' && characterLevel && spell.levelData) {
      const thresholds = Object.keys(spell.levelData).map(Number).sort((left, right) => left - right);
      let bestThreshold = null;
      thresholds.forEach((threshold) => {
        if (characterLevel >= threshold) bestThreshold = threshold;
      });
      if (bestThreshold !== null && spell.levelData?.[bestThreshold]?.dice) {
        return spell.levelData[bestThreshold].dice;
      }
      return spell.dice || '';
    }

    if (spell.levelData?.[resolvedLevel]?.dice) {
      return spell.levelData[resolvedLevel].dice;
    }

    if (!spell.dice) return '';

    const upcastDice = (spell.upcastPattern === 'dice_per_level' || spell.upcastDice)
      ? (spell.upcastDice || '')
      : '';
    if (levelsAbove <= 0 || !upcastDice) {
      return spell.dice;
    }

    const baseMatch = spell.dice.match(/(\d+)?d(\d+)/);
    const upcastMatch = upcastDice.match(/(\d+)?d(\d+)/);
    if (baseMatch && upcastMatch && baseMatch[2] === upcastMatch[2]) {
      const baseCount = parseInt(baseMatch[1], 10) || 1;
      const upcastCount = (parseInt(upcastMatch[1], 10) || 1) * levelsAbove;
      return `${baseCount + upcastCount}d${baseMatch[2]}`;
    }

    return spell.dice;
  })();
  if (resolvedDice) {
    resolvedSpell.dice = resolvedDice;
  }

  resolvedSpell._resolvedLevel = resolvedLevel;
  return resolvedSpell;
}

export function buildSpellCastRequest({
  char,
  spell,
  systems = {},
  surface = SPELL_BINDING_SURFACE_SHEET,
  castLevel = null,
  source = null,
  manualCostOverride = null,
  settingsOverride = null,
  spellSlotsOverride = null,
  usedSlotsOverride = null,
  currentManaOverride = null,
  maxSpellLevelOverride = null,
  freeCastsUsedTodayOverride = null,
  freeCastsPerDayOverride = null,
} = {}) {
  const liveChar = char || {};
  const settings = syncSpellSettings(settingsOverride || liveChar.settings || {});
  const spellSlots = spellSlotsOverride || liveChar.spellSlots || { 1: 2 };
  const usedSlots = usedSlotsOverride || liveChar.usedSpellSlots || {};
  const resolvedCastLevel = Number.isFinite(castLevel) ? castLevel : (spell?.level || 0);

  return {
    char: liveChar,
    spell,
    systems,
    settings,
    source: source || surface || SPELL_BINDING_SURFACE_SHEET,
    surface,
    castLevel: resolvedCastLevel,
    spellSlots,
    usedSlots,
    currentMana: Number.isFinite(currentManaOverride) ? currentManaOverride : (liveChar.currentMana || 0),
    maxSpellLevel: Number.isFinite(maxSpellLevelOverride)
      ? maxSpellLevelOverride
      : (getScalarSpellProgressionMaxCastLevel(settings, getMaxSpellLevel(spellSlots)) ?? 0),
    freeCastsUsedToday: Number.isFinite(freeCastsUsedTodayOverride) ? freeCastsUsedTodayOverride : (liveChar.freeCastsUsedToday || 0),
    freeCastsPerDay: Number.isFinite(freeCastsPerDayOverride) ? freeCastsPerDayOverride : (settings.freeCastsPerDay || 4),
    manualCostOverride: Number.isFinite(manualCostOverride) ? Math.max(0, manualCostOverride) : null,
    resolvedSpell: resolveSpellAtLevel(spell, resolvedCastLevel, liveChar.level),
  };
}

export function quoteSpellCast(request = {}) {
  const castInfo = getSpellCastInfo(request);
  const warningFlags = [];
  if (castInfo?.freeAvailable) warningFlags.push('free-cast');
  if (castInfo?.overcast) warningFlags.push('overcast');
  if (castInfo?.droughtWarning) warningFlags.push('mana-drought');
  if (castInfo?.tooHigh) warningFlags.push('too-high');

  return {
    ...castInfo,
    request,
    source: request.source || request.surface || SPELL_BINDING_SURFACE_SHEET,
    spell: request.spell,
    resolvedSpell: request.resolvedSpell || resolveSpellAtLevel(request.spell, request.castLevel, request.char?.level),
    warnings: warningFlags,
    quoteKind: castInfo?.kind || 'unknown',
  };
}

function normalizeResourceFollowUpReturn(followUpReturn, fallbackChar) {
  if (
    followUpReturn
    && typeof followUpReturn === 'object'
    && !Array.isArray(followUpReturn)
    && Object.prototype.hasOwnProperty.call(followUpReturn, 'nextChar')
  ) {
    return {
      nextChar: followUpReturn.nextChar || fallbackChar,
      resourceFollowUpResults: followUpReturn.results || followUpReturn.resourceFollowUpResults || null,
    };
  }

  return {
    nextChar: followUpReturn || fallbackChar,
    resourceFollowUpResults: null,
  };
}

function buildResourceEvents(kind, amount, extra = {}) {
  if (!Number.isFinite(amount) || amount === 0) return [];
  return [{
    kind: 'spend',
    amount,
    resource: kind,
    ...extra,
  }];
}

function applyLegacyManaPayment(char, castQuote) {
  const settings = castQuote?.request?.settings || {};
  const castLevel = castQuote?.castLevel ?? castQuote?.request?.castLevel ?? 0;
  let nextChar = { ...(char || {}) };

  if (castLevel === 0) {
    return { nextChar, resourceEvents: [] };
  }

  if (castQuote.freeAvailable) {
    nextChar.freeCastsUsedToday = (nextChar.freeCastsUsedToday || 0) + 1;
    return { nextChar, resourceEvents: [] };
  }

  const nextMana = (nextChar.currentMana || 0) - (castQuote.cost || 0);
  nextChar.currentMana = nextMana;

  if (castQuote.overcast && settings.manaSicknessEnabled) {
    const sicknessRounds = Math.floor(Math.random() * 4) + 1;
    nextChar.manaSickness = true;
    nextChar.manaSicknessRounds = (nextChar.manaSicknessRounds || 0) + sicknessRounds;
  }
  if (nextMana < 0 && settings.manaDroughtEnabled) {
    const overkill = Math.abs(nextMana);
    nextChar.manaDrought = true;
    nextChar.exhaustionLevel = overkill > 0
      ? Math.min(10, (nextChar.exhaustionLevel || 0) + Math.ceil(overkill))
      : (nextChar.exhaustionLevel || 0);
  }

  return {
    nextChar,
    resourceEvents: buildResourceEvents('legacy-mana', castQuote.cost || 0),
  };
}

function applyLegacySlotPayment(char, castQuote) {
  const castLevel = castQuote?.castLevel ?? castQuote?.request?.castLevel ?? 0;
  if (castLevel === 0) {
    return { nextChar: { ...(char || {}) }, resourceEvents: [] };
  }

  const nextChar = {
    ...(char || {}),
    usedSpellSlots: { ...((char || {}).usedSpellSlots || {}) },
  };
  nextChar.usedSpellSlots[castLevel] = (nextChar.usedSpellSlots[castLevel] || 0) + 1;

  return {
    nextChar,
    resourceEvents: buildResourceEvents('legacy-slots', 1, { level: castLevel }),
  };
}

export function applySpellResourcePayment({
  char,
  systems = {},
  castQuote,
  applyResourceFollowUps = null,
} = {}) {
  if (!castQuote) {
    return { nextChar: char, resourceEvents: [] };
  }

  let paymentResult = { nextChar: char, resourceEvents: [] };
  if (castQuote.kind === 'system-scalar' || castQuote.kind === 'system-tiered') {
    let nextChar = char;
    if (castQuote.consumesFreeCast) {
      nextChar = {
        ...(nextChar || {}),
        freeCastsUsedToday: ((nextChar || {}).freeCastsUsedToday || 0) + 1,
      };
    }
    if ((castQuote.castLevel || 0) > 0 && castQuote.quote?.ok) {
      const spendResult = applyBoundResourceSpend(nextChar, systems, castQuote.binding, castQuote.quote);
      paymentResult = {
        nextChar: spendResult.nextChar,
        resourceEvents: spendResult.resourceEvents || [],
      };
    } else {
      paymentResult = {
        nextChar,
        resourceEvents: [],
      };
    }
  } else if (castQuote.kind === 'legacy-mana') {
    paymentResult = applyLegacyManaPayment(char, castQuote);
  } else if (castQuote.kind === 'legacy-slots') {
    paymentResult = applyLegacySlotPayment(char, castQuote);
  }

  if (typeof applyResourceFollowUps === 'function' && paymentResult.resourceEvents?.length) {
    const followUps = normalizeResourceFollowUpReturn(
      applyResourceFollowUps(paymentResult.nextChar, paymentResult.resourceEvents),
      paymentResult.nextChar,
    );
    return {
      ...paymentResult,
      nextChar: followUps.nextChar,
      resourceFollowUpResults: followUps.resourceFollowUpResults,
    };
  }

  return {
    ...paymentResult,
    resourceFollowUpResults: null,
  };
}

export function appendSpellHistory(char, spell, castQuote, { historyLimit = 50 } = {}) {
  const nextChar = { ...(char || {}) };
  const castLevel = castQuote?.castLevel ?? castQuote?.request?.castLevel ?? (spell?.level || 0);
  nextChar.spellHistory = [
    {
      spellId: spell?.id,
      spellName: spell?.name,
      castAt: new Date().toISOString(),
      level: castLevel,
      manaCost: castQuote?.cost ?? 0,
    },
    ...((nextChar.spellHistory || []).slice(0, Math.max(0, historyLimit - 1))),
  ];
  return nextChar;
}

export function finalizeSpellCastCommit({
  char,
  spell,
  systems = {},
  castQuote,
  applyResourceFollowUps = null,
  beforeFinalize = null,
  runSpellCastTrigger = null,
  historyLimit = 50,
} = {}) {
  const paymentResult = applySpellResourcePayment({
    char,
    systems,
    castQuote,
    applyResourceFollowUps,
  });

  let nextChar = paymentResult.nextChar;
  if (typeof beforeFinalize === 'function') {
    nextChar = beforeFinalize(nextChar, paymentResult.resourceEvents || []);
  }

  nextChar = appendSpellHistory(nextChar, spell, castQuote, { historyLimit });

  if (typeof runSpellCastTrigger === 'function') {
    nextChar = runSpellCastTrigger(nextChar, paymentResult.resourceEvents || []);
  }

  return {
    nextChar,
    resourceEvents: paymentResult.resourceEvents || [],
    resourceFollowUpResults: paymentResult.resourceFollowUpResults || null,
  };
}

export function resetSpellResourcesForRest({
  char,
  systems = {},
  restType,
  settings = null,
  surface = SPELL_RESOURCE_SURFACE_ANY,
  applyResourceFollowUps = null,
} = {}) {
  const effectiveChar = char || {};
  const spellResourceUi = getSpellResourceUiModel({
    char: effectiveChar,
    systems,
    settings,
    surface,
  });

  let nextChar = effectiveChar;
  if (restType === 'longRest') {
    nextChar = {
      ...nextChar,
      freeCastsUsedToday: 0,
      manaSickness: false,
      manaSicknessRounds: 0,
      manaDrought: false,
      concentratingOn: null,
      concentrationEffectGroupId: null,
      exhaustionLevel: Math.max(0, (effectiveChar.exhaustionLevel || 0) - 1),
    };
  }
  if (spellResourceUi.usesLegacyManaAsPrimary) {
    const maxMana = spellResourceUi.summary.max ?? 0;
    if (restType === 'longRest') {
      nextChar = { ...nextChar, currentMana: maxMana };
    } else if (restType === 'shortRest') {
      nextChar = {
        ...nextChar,
        currentMana: Math.min(maxMana, (nextChar.currentMana || 0) + Math.floor(maxMana / 2)),
      };
    }
  }
  if (spellResourceUi.usesLegacySlotsAsPrimary && restType === 'longRest') {
    nextChar = { ...nextChar, usedSpellSlots: {} };
  }

  const recovery = recoverDirectSpellResources(nextChar, systems, restType, SPELL_BINDING_SURFACE_ANY);
  nextChar = recovery.nextChar;

  if (typeof applyResourceFollowUps === 'function' && recovery.resourceEvents?.length) {
    const followUps = normalizeResourceFollowUpReturn(
      applyResourceFollowUps(nextChar, recovery.resourceEvents),
      nextChar,
    );
    nextChar = followUps.nextChar;
    recovery.resourceFollowUpResults = followUps.resourceFollowUpResults;
  }

  return {
    nextChar,
    resourceEvents: recovery.resourceEvents || [],
    resourceFollowUpResults: recovery.resourceFollowUpResults || null,
    bindings: recovery.bindings || [],
    spellResourceUi,
  };
}
